import { makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { WrapperNodeProps, WrapperNodeConstructorProps } from "@corca/data";

export class WrapperNodeModel extends BaseNodeModel<WrapperNodeProps> implements VisitableNode {
  static type = "wrapper" as const;

  type = WrapperNodeModel.type;

  protected fillRequiredProps(props: WrapperNodeConstructorProps): WrapperNodeProps {
    return props;
  }

  setProps(props: Partial<WrapperNodeProps>) {
    const computedChanged = props.computed !== this.props.computed;
    super.setProps(props);
    if (computedChanged) {
      this.doc.computedNodes.scheduleRecalculation();
    }
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<WrapperNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      inlineNode: true,
      isEmpty: true,
    });
  }

  static create(
    doc: DocumentModel,
    node: InlineNodeModel,
    id?: NodeId,
    props: WrapperNodeConstructorProps = {},
  ) {
    return new WrapperNodeModel(doc, id, props, [node]);
  }

  get inlineNode() {
    return this.children.find((n) => n.is(InlineNodeModel));
  }

  get isEmpty() {
    return this.descendants.length <= 1;
  }

  get keepWhenEmpty(): boolean {
    return !!this.props.keepWhenEmpty;
  }

  get computed(): boolean {
    return !!this.props.computed;
  }

  get refId(): string | undefined {
    return this.props.refId;
  }

  /** Rendered inside a locked block; see `EditorModel.lockedRootIds`. */
  get locked(): boolean {
    return this.doc.editor.lockedRootIds.has(this.id);
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitWrapperNode(this);
  }
}
